import type React from "react";
import { useState } from "react";
import { validateFieldValue } from "../../config/fieldConfig/formValidation";
import { OPTION_LISTS } from "../../config/fieldConfig/optionLists";
import { useFieldResolver } from "../../hooks/useFieldResolver";
import type { KYCFormData } from "../../types";
import { FileUploadField } from "../form/FileUploadField";
import { FormField } from "../form/FormField";
import { FormSection } from "../form/FormSection";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

interface Step1Props {
	formData: KYCFormData;
	updateFormData: (updates: Partial<KYCFormData>) => void;
}

const PROOF_OF_BUSINESS_TYPES = [
	"Certificate of Incorporation",
	"Business Registration Certificate",
	"Business License",
	"Articles of Organization",
	"Partnership Agreement",
	"Trust Document",
];

const PROOF_OF_ADDRESS_TYPES = [
	"Utility Bill",
	"Bank Statement",
	"Lease Agreement",
	"Government-issued Document",
];

const CUSTOM_FIELDS = new Set(["registeredAddress", "proofOfBusinessUrl"]);

const Step1CompanyBasics: React.FC<Step1Props> = ({
	formData,
	updateFormData,
}) => {
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const { fields } = useFieldResolver(
		{
			incorporationCountry: formData.countryOfIncorporation,
			entityType: formData.entityType,
			flowType: "KYB",
		},
		formData as unknown as Record<string, unknown>,
	);

	const step1Fields = fields.filter((f) => f.step === 1);
	const companyBasicsFields = step1Fields.filter(
		(f) => f.section === "company_basics" && !CUSTOM_FIELDS.has(f.fieldId),
	);
	const registeredAddressField = step1Fields.find(
		(f) => f.fieldId === "registeredAddress",
	);
	const proofField = step1Fields.find(
		(f) => f.fieldId === "proofOfBusinessUrl",
	);

	const formValues = formData as unknown as Record<string, unknown>;

	const handleChange = (fieldId: string, value: unknown) => {
		updateFormData({ [fieldId]: value } as Partial<KYCFormData>);
		if (fieldErrors[fieldId]) {
			setFieldErrors((prev) => {
				const next = { ...prev };
				delete next[fieldId];
				return next;
			});
		}
	};

	const handleBlur = (fieldId: string) => {
		const field = companyBasicsFields.find((f) => f.fieldId === fieldId);
		if (!field) return;
		const err = validateFieldValue(field, formValues[fieldId], formValues);
		setFieldErrors((prev) => ({ ...prev, [fieldId]: err ?? "" }));
	};

	const threeColIds = [
		"entityType",
		"countryOfIncorporation",
		"dateOfIncorporation",
	];
	const beforeRow = companyBasicsFields.filter(
		(f) => !threeColIds.includes(f.fieldId) && f.order < 50,
	);
	const threeCol = companyBasicsFields.filter((f) =>
		threeColIds.includes(f.fieldId),
	);
	const afterRow = companyBasicsFields.filter(
		(f) => !threeColIds.includes(f.fieldId) && f.order >= 50,
	);

	return (
		<div className="w-full">
			<FormSection title="Company Basics">
				{beforeRow.map((field) => (
					<FormField
						key={field.fieldId}
						config={field}
						value={formValues[field.fieldId]}
						onChange={handleChange}
						onBlur={handleBlur}
						error={fieldErrors[field.fieldId] || undefined}
					/>
				))}
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					{threeCol.map((field) => (
						<FormField
							key={field.fieldId}
							config={field}
							value={formValues[field.fieldId]}
							onChange={handleChange}
							onBlur={handleBlur}
							error={fieldErrors[field.fieldId] || undefined}
						/>
					))}
				</div>
				{afterRow.map((field) => (
					<FormField
						key={field.fieldId}
						config={field}
						value={formValues[field.fieldId]}
						onChange={handleChange}
						onBlur={handleBlur}
						error={fieldErrors[field.fieldId] || undefined}
					/>
				))}
			</FormSection>

			{registeredAddressField && (
				<FormSection title="Registered Address">
					<div className="space-y-1.5">
						<Label htmlFor="address-line-1">
							Address Line 1
							<span
								aria-hidden="true"
								className="ml-0.5 text-(--color-error-500)"
							>
								*
							</span>
						</Label>
						<Input
							id="address-line-1"
							type="text"
							value={formData.registeredAddress.address1}
							onChange={(e) =>
								updateFormData({
									registeredAddress: {
										...formData.registeredAddress,
										address1: e.target.value,
									},
								})
							}
						/>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="address-line-2">Address Line 2</Label>
						<Input
							id="address-line-2"
							type="text"
							value={formData.registeredAddress.address2}
							onChange={(e) =>
								updateFormData({
									registeredAddress: {
										...formData.registeredAddress,
										address2: e.target.value,
									},
								})
							}
						/>
					</div>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="city">
								City
								<span
									aria-hidden="true"
									className="ml-0.5 text-(--color-error-500)"
								>
									*
								</span>
							</Label>
							<Input
								id="city"
								type="text"
								value={formData.registeredAddress.city}
								onChange={(e) =>
									updateFormData({
										registeredAddress: {
											...formData.registeredAddress,
											city: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="state-province">
								State/Province
								<span
									aria-hidden="true"
									className="ml-0.5 text-(--color-error-500)"
								>
									*
								</span>
							</Label>
							<Input
								id="state-province"
								type="text"
								value={formData.registeredAddress.stateProvince}
								onChange={(e) =>
									updateFormData({
										registeredAddress: {
											...formData.registeredAddress,
											stateProvince: e.target.value,
										},
									})
								}
							/>
						</div>
					</div>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="zip-postal-code">
								ZIP/Postal Code
								<span
									aria-hidden="true"
									className="ml-0.5 text-(--color-error-500)"
								>
									*
								</span>
							</Label>
							<Input
								id="zip-postal-code"
								type="text"
								value={formData.registeredAddress.zip}
								onChange={(e) =>
									updateFormData({
										registeredAddress: {
											...formData.registeredAddress,
											zip: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="country">
								Country
								<span
									aria-hidden="true"
									className="ml-0.5 text-(--color-error-500)"
								>
									*
								</span>
							</Label>
							<Select
								value={formData.registeredAddress.country}
								onValueChange={(value) =>
									updateFormData({
										registeredAddress: {
											...formData.registeredAddress,
											country: value,
										},
									})
								}
							>
								<SelectTrigger id="country">
									<SelectValue placeholder="Select country" />
								</SelectTrigger>
								<SelectContent>
									{OPTION_LISTS.COUNTRIES_ISO.map((opt) => (
										<SelectItem key={opt.value} value={opt.value}>
											{opt.label.en}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</FormSection>
			)}

			{proofField && (
				<FormSection title="Supporting Documents">
					<div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
						<FileUploadField
							id="proof-of-business"
							label="Proof of Business"
							required
							description={`Upload one of: ${PROOF_OF_BUSINESS_TYPES.join(", ")}`}
							value={formData.proofOfBusinessUrl}
							onUpload={(key) => updateFormData({ proofOfBusinessUrl: key })}
							onRemove={() => updateFormData({ proofOfBusinessUrl: undefined })}
						/>
						<FileUploadField
							id="proof-of-address"
							label="Proof of Address"
							required
							description={`Upload: ${PROOF_OF_ADDRESS_TYPES.join(", ")} (with company name and address)`}
							value={formData.proofOfAddressUrl}
							onUpload={(key) => updateFormData({ proofOfAddressUrl: key })}
							onRemove={() => updateFormData({ proofOfAddressUrl: undefined })}
						/>
					</div>
				</FormSection>
			)}
		</div>
	);
};

export default Step1CompanyBasics;
